import { $host } from '../../http';

import {
    deleteAnalyticsEvents,
    getOldestAnalyticsEvents
} from './eventQueue';

const TRACK_ENDPOINT =
    '/api/analytics/track/v2';

const BATCH_SIZE =
    25;

const MAX_BATCHES_PER_FLUSH =
    4;

const FLUSH_INTERVAL_MS =
    5000;

const TERMINAL_STATUSES =
    new Set([
        'stored',
        'duplicate',
        'quarantined'
    ]);

let flushPromise = null;
let intervalId = null;
let listenersStarted = false;

function canUseNetwork() {
    return (
        typeof navigator ===
        'undefined' ||

        navigator.onLine !==
        false
    );
}

async function sendOneBatch() {
    const queued =
        await getOldestAnalyticsEvents(
            BATCH_SIZE
        );

    if (!queued.length) {
        return {
            sent: 0,
            mayHaveMore: false
        };
    }

    const clientSentAt =
        new Date()
            .toISOString();

    const events =
        queued.map(
            item => ({
                ...item.rawEvent,
                clientSentAt
            })
        );

    const { data } =
        await $host.post(
            TRACK_ENDPOINT,
            {
                events
            }
        );

    const results =
        Array.isArray(
            data?.results
        )
            ? data.results
            : [];

    const acknowledgedIds =
        [];

    for (
        const result
        of results
        ) {
        if (
            !result?.eventId ||

            !TERMINAL_STATUSES
                .has(
                    result.status
                )
        ) {
            continue;
        }

        acknowledgedIds.push(
            result.eventId
        );

        if (
            result.status ===
            'quarantined'
        ) {
            console.warn(
                '[AnalyticsV2] Event quarantined:',
                result.eventId,
                result.errorCode || ''
            );
        }
    }

    if (
        acknowledgedIds.length
    ) {
        await deleteAnalyticsEvents(
            acknowledgedIds
        );
    }

    return {
        sent:
        acknowledgedIds.length,

        mayHaveMore:
            queued.length ===
            BATCH_SIZE
    };
}

async function runFlush() {
    if (!canUseNetwork()) {
        return;
    }

    for (
        let i = 0;
        i <
        MAX_BATCHES_PER_FLUSH;
        i += 1
    ) {
        const result =
            await sendOneBatch();

        if (
            !result.mayHaveMore ||
            result.sent === 0
        ) {
            break;
        }
    }
}

export function flushAnalyticsQueue() {
    if (flushPromise) {
        return flushPromise;
    }

    flushPromise =
        runFlush()
            .catch(error => {
                /*
                 * Ничего не удаляем.
                 * Следующий flush
                 * попробует снова.
                 */
                if (
                    error?.response
                        ?.status
                ) {
                    console.warn(
                        '[AnalyticsV2] Flush failed:',
                        error.response
                            .status,

                        error.response
                            ?.data ||
                        error.message
                    );
                } else if (
                    canUseNetwork()
                ) {
                    console.warn(
                        '[AnalyticsV2] Flush failed:',
                        error?.message ||
                        error
                    );
                }
            })
            .finally(() => {
                flushPromise =
                    null;
            });

    return flushPromise;
}

function handleOnline() {
    flushAnalyticsQueue();
}

function handleVisibilityChange() {
    if (
        document.visibilityState ===
        'visible'
    ) {
        flushAnalyticsQueue();
    }
}

function handlePageHide() {
    /*
     * Если axios не успеет —
     * событие всё равно останется
     * в IndexedDB.
     */
    flushAnalyticsQueue();
}

export function startAnalyticsSender() {
    if (
        typeof window ===
        'undefined' ||
        listenersStarted
    ) {
        return;
    }

    listenersStarted =
        true;

    window.addEventListener(
        'online',
        handleOnline
    );

    window.addEventListener(
        'pagehide',
        handlePageHide
    );

    document.addEventListener(
        'visibilitychange',
        handleVisibilityChange
    );

    intervalId =
        window.setInterval(
            flushAnalyticsQueue,
            FLUSH_INTERVAL_MS
        );

    flushAnalyticsQueue();
}

export function stopAnalyticsSender() {
    if (
        typeof window ===
        'undefined' ||
        !listenersStarted
    ) {
        return;
    }

    listenersStarted =
        false;

    window.removeEventListener(
        'online',
        handleOnline
    );

    window.removeEventListener(
        'pagehide',
        handlePageHide
    );

    document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
    );

    if (
        intervalId !==
        null
    ) {
        window.clearInterval(
            intervalId
        );

        intervalId =
            null;
    }
}